/**
 * FieldTracker — collapsible panel showing collected vs pending fields.
 *
 * Displays in real-time as the AI emits field values (Quiet Trust theme).
 * Collected fields show a teal-green check; pending show a muted outline circle.
 * An "All done" banner appears when complete.
 */

import React, { useState } from 'react';

interface FieldStatus {
  key: string;
  label: string;
  collected: boolean;
  value?: unknown;
}

interface Props {
  fields: FieldStatus[];
  isComplete: boolean;
  collectedCount: number;
  totalRequired: number;
}

// --- Inline SVG icons (no emoji) -------------------------------------------

function CheckIcon({ size = 12, color = 'var(--ci-success)' }: { size?: number; color?: string }): React.ReactElement {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <polyline points="20 6 9 17 4 12" />
    </svg>
  );
}

function PendingIcon(): React.ReactElement {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ci-text-muted)" strokeWidth="2" aria-hidden="true">
      <circle cx="12" cy="12" r="9" />
    </svg>
  );
}

function ChevronIcon({ expanded }: { expanded: boolean }): React.ReactElement {
  return (
    <svg
      width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--ci-text-muted)"
      strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"
      style={{ transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s ease' }}
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

export default function FieldTracker({ fields, isComplete, collectedCount, totalRequired }: Props): React.ReactElement {
  const [expanded, setExpanded] = useState(false);

  if (totalRequired === 0) return <></>;

  const pct = totalRequired > 0 ? Math.round((collectedCount / totalRequired) * 100) : 0;

  return (
    <div style={styles.container}>
      {/* Summary bar */}
      <button style={styles.header} onClick={() => setExpanded((v) => !v)} aria-expanded={expanded}>
        <div style={styles.headerLeft}>
          {isComplete ? (
            <span style={styles.completeLabel}>
              <CheckIcon size={14} />
              All done
            </span>
          ) : (
            <span style={styles.progressLabel}>
              {collectedCount} / {totalRequired} fields collected
            </span>
          )}
        </div>
        <div style={styles.headerRight}>
          <div style={styles.progressBar}>
            <div style={{ ...styles.progressFill, width: `${pct}%`, background: isComplete ? 'var(--ci-success)' : 'var(--ci-primary)' }} />
          </div>
          <span style={styles.chevron}><ChevronIcon expanded={expanded} /></span>
        </div>
      </button>

      {/* Field list */}
      {expanded && (
        <div style={styles.list}>
          {fields.map((f) => (
            <div key={f.key} style={styles.fieldRow}>
              <span style={styles.icon}>
                {f.collected ? <CheckIcon /> : <PendingIcon />}
              </span>
              <span style={{ ...styles.fieldLabel, color: f.collected ? 'var(--ci-text)' : 'var(--ci-text-muted)' }}>
                {f.label}
              </span>
              {f.collected && f.value !== undefined && f.value !== null && (
                <span style={styles.fieldValue}>
                  {formatValue(f.value)}
                </span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatValue(val: unknown): string {
  if (val === true) return 'Yes';
  if (val === false) return 'No';
  if (typeof val === 'string') {
    return val.length > 40 ? `${val.slice(0, 37)}...` : val;
  }
  return String(val);
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    margin:       '8px 0 4px',
    border:       '1px solid var(--ci-border-light)',
    borderRadius: 'var(--ci-radius-lg)',
    overflow:     'hidden',
    background:   'var(--ci-surface)',
    fontFamily:   'var(--ci-font)',
  },
  header: {
    width:          '100%',
    display:        'flex',
    alignItems:     'center',
    justifyContent: 'space-between',
    padding:        '8px 12px',
    background:     'none',
    border:         'none',
    cursor:         'pointer',
    gap:            12,
    fontFamily:     'var(--ci-font)',
  },
  headerLeft:     { flex: 1, textAlign: 'left' },
  headerRight:    { display: 'flex', alignItems: 'center', gap: 8 },
  completeLabel:  { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 600, color: 'var(--ci-success)' },
  progressLabel:  { fontSize: 12, color: 'var(--ci-text-muted)' },
  progressBar:    { width: 80, height: 4, background: 'var(--ci-border-light)', borderRadius: 4, overflow: 'hidden' },
  progressFill:   { height: '100%', borderRadius: 4, transition: 'width 0.3s ease' },
  chevron:        { display: 'inline-flex', alignItems: 'center' },
  list: {
    borderTop:     '1px solid var(--ci-border-light)',
    padding:       '6px 12px 8px',
    display:       'flex',
    flexDirection: 'column',
    gap:           3,
  },
  fieldRow: {
    display:    'flex',
    alignItems: 'center',
    gap:        6,
    padding:    '2px 0',
  },
  icon:       { display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 14, flexShrink: 0 },
  fieldLabel: { fontSize: 12, flex: 1 },
  fieldValue: { fontSize: 11, color: 'var(--ci-text-body)', maxWidth: 120, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
};
